import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  Schedule,
  Refresh,
  EventNote,
  ViewList,
  ChevronLeft,
  ChevronRight,
  InfoOutlined,
  CheckCircleOutline,
  ErrorOutline,
  AccessTime,
  PlayCircle
} from '@mui/icons-material';
import axios from 'axios';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths,
  addWeeks,
  subWeeks,
  parseISO,
  isAfter,
  addDays,
  isValid
} from 'date-fns';
import { fadeInUp } from '../styles/sharedStyles';

const TaskScheduler = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filters, setFilters] = useState({
    immich: true,
    jellyfin: true,
    truenas: true,
    ha: true,
    rpi: true
  });

  const toggleFilter = (service) => {
    setFilters(prev => ({ ...prev, [service]: !prev[service] }));
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => filters[task.service] !== false);
  }, [tasks, filters]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/scheduler/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to fetch scheduled tasks');
    } finally {
      setLoading(false);
    }
  };

  const runTask = async (task) => {
    // Only support TrueNAS tasks for now in this view
    if (task.service !== 'truenas') {
      alert(`⚠️ Action not supported for ${task.service} tasks yet.`);
      return;
    }

    // Parse type and id from truenas-type-id
    const parts = task.id.split('-');
    if (parts.length < 3) return;
    
    const type = parts[1]; // cloudsync, snapshot, or cron
    const id = parts[2];
    
    // Map 'cron' to 'cronjob' for the backend
    const backendType = type === 'cron' ? 'cronjob' : type;

    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/truenas/tasks/${backendType}/${id}/run`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        setTimeout(fetchTasks, 2000);
      } else {
        alert(`⚠️ ${response.data.message}`);
      }
    } catch (err) {
      console.error(`Error running task:`, err);
      alert(`❌ Failed to run task: ${err.response?.data?.detail || err.message}`);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [apiBaseUrl, token]);

  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      if (!a.next_run && !b.next_run) return 0;
      if (!a.next_run) return 1;
      if (!b.next_run) return -1;
      
      // Sort by hour and minute
      const dateA = parseISO(a.next_run);
      const dateB = parseISO(b.next_run);
      return (dateA.getHours() * 60 + dateA.getMinutes()) - (dateB.getHours() * 60 + dateB.getMinutes());
    });
  }, [filteredTasks]);

  const getStatusColor = (status) => {
    if (!status) return theme.palette.text.secondary;
    const s = status.toLowerCase();
    if (s === 'running' || s === 'active') return theme.palette.info.main;
    if (s === 'idle' || s === 'success' || s === 'enabled' || s === 'completed') return theme.palette.success.main;
    if (s === 'failed' || s === 'error' || s === 'disabled') return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'immich': return '/immich.png';
      case 'jellyfin': return '/jellyfin.png';
      case 'truenas': return '/truenas_logo.png';
      case 'ha': return '/home-assistant-alt.png';
      case 'rpi': return '/raspberry-pi.png';
      default: return null;
    }
  };

  const formatSafeDate = (dateStr, formatStr = 'PPp') => {
    if (!dateStr) return 'N/A';
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return 'Invalid Date';
      return format(date, formatStr);
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const renderTaskCard = (task, index) => (
    <Card
      key={task.id}
      sx={{
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        transition: 'all 0.3s ease',
        animation: `${fadeInUp} 0.5s ease-out`,
        animationDelay: `${index * 0.05}s`,
        animationFillMode: 'both',
        '&:hover': {
          transform: 'translateY(-4px)',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            src={getServiceIcon(task.service)}
            sx={{ 
              width: 48, 
              height: 48, 
              p: 0.5, 
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px'
            }}
          />
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600 }}>
              {task.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 0.5 }}>
              {task.description}
            </Typography>
          </Box>
          <Chip
            label={task.status || 'Unknown'}
            size="small"
            sx={{
              backgroundColor: alpha(getStatusColor(task.status), 0.1),
              color: getStatusColor(task.status),
              border: `1px solid ${alpha(getStatusColor(task.status), 0.3)}`,
              fontWeight: 600,
              textTransform: 'capitalize'
            }}
          />
          {task.service === 'truenas' && (
            <Tooltip title="Run Now">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  runTask(task);
                }}
                sx={{ 
                  ml: 1, 
                  color: '#00ff88',
                  background: 'rgba(0, 255, 136, 0.05)',
                  '&:hover': { background: 'rgba(0, 255, 136, 0.2)' }
                }}
              >
                <PlayCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
              <Schedule sx={{ fontSize: 18, mr: 1, color: '#00d4ff' }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                Schedule: <strong>{task.schedule || 'Manual'}</strong>
              </Typography>
            </Box>
          </Grid>
          {task.last_run && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
                <CheckCircleOutline sx={{ fontSize: 16, mr: 1 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Last run: {formatSafeDate(task.last_run)}
                </Typography>
              </Box>
            </Grid>
          )}
          {task.next_run && (
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: isAfter(new Date(), parseISO(task.next_run)) ? '#ff4444' : '#00ff88',
                background: alpha(isAfter(new Date(), parseISO(task.next_run)) ? '#ff4444' : '#00ff88', 0.05),
                p: 1,
                borderRadius: '8px'
              }}>
                <AccessTime sx={{ fontSize: 18, mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Next run: {formatSafeDate(task.next_run)}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  // Calendar Components
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const getTasksForDay = (day) => {
    return filteredTasks
      .filter(task => {
        if (task.next_run) {
          return isSameDay(parseISO(task.next_run), day);
        }
        return false;
      })
      .sort((a, b) => {
        const dateA = parseISO(a.next_run);
        const dateB = parseISO(b.next_run);
        return (dateA.getHours() * 60 + dateA.getMinutes()) - (dateB.getHours() * 60 + dateB.getMinutes());
      });
  };

  const renderFilters = () => (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      mb: 3, 
      flexWrap: 'wrap',
      background: 'rgba(255, 255, 255, 0.03)',
      p: 1.5,
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.05)'
    }}>
      {Object.keys(filters).map(service => (
        <Tooltip key={service} title={`Toggle ${service.toUpperCase()}`}>
          <Box
            onClick={() => toggleFilter(service)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: filters[service] ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              border: `1px solid ${filters[service] ? alpha(theme.palette.primary.main, 0.3) : 'rgba(255, 255, 255, 0.1)'}`,
              opacity: filters[service] ? 1 : 0.5,
              '&:hover': {
                background: filters[service] ? alpha(theme.palette.primary.main, 0.2) : 'rgba(255, 255, 255, 0.05)',
                opacity: 1
              }
            }}
          >
            <img src={getServiceIcon(service)} alt="" style={{ width: 18, height: 18 }} />
            <Typography variant="caption" sx={{ 
              color: '#fff', 
              fontWeight: 600, 
              textTransform: 'uppercase',
              fontSize: '0.65rem'
            }}>
              {service}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );

  const renderCalendar = () => {
    const calendarStart = isMobile ? currentDate : startOfWeek(currentDate);
    const calendarEnd = isMobile ? currentDate : endOfWeek(calendarStart);
    const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
              {isMobile 
                ? format(currentDate, 'EEEE, MMM d') 
                : `${format(calendarStart, 'MMM d')} - ${format(calendarEnd, 'MMM d, yyyy')}`
              }
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              {isMobile ? 'Daily View' : 'Weekly View'}
            </Typography>
          </Box>
          <Box>
            <IconButton 
              onClick={() => setCurrentDate(isMobile ? addDays(currentDate, -1) : subWeeks(currentDate, 1))} 
              sx={{ color: '#fff' }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={() => setCurrentDate(new Date())} sx={{ color: '#00d4ff', fontSize: '0.9rem' }}>
              Today
            </IconButton>
            <IconButton 
              onClick={() => setCurrentDate(isMobile ? addDays(currentDate, 1) : addWeeks(currentDate, 1))} 
              sx={{ color: '#fff' }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>

        <Grid container columns={isMobile ? 1 : 7} sx={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', overflow: 'hidden' }}>
          {!isMobile && days.map(day => (
            <Grid item xs={1} key={day} sx={{ 
              p: 1.5, 
              textAlign: 'center', 
              background: 'rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'uppercase'
            }}>
              {day}
            </Grid>
          ))}
          {calendarDays.map((day, idx) => {
            const dayTasks = getTasksForDay(day);
            const isToday = isSameDay(day, new Date());

            return (
              <Grid item xs={1} key={idx} sx={{ 
                height: isMobile ? 'auto' : { xs: 600, sm: 1200 }, 
                minHeight: isMobile ? 400 : 'unset',
                p: 0.5,
                background: isToday ? 'rgba(0, 212, 255, 0.05)' : 'transparent',
                borderRight: !isMobile && (idx + 1) % 7 === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.05)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.02)'
                }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 1,
                  mb: 1
                }}>
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.8rem',
                    color: isToday ? '#00d4ff' : 'rgba(255, 255, 255, 0.7)',
                    fontWeight: isToday ? 700 : 400
                  }}>
                    {isMobile ? format(day, 'EEEE, MMM d') : format(day, 'd')}
                  </Typography>
                  {isToday && (
                    <Chip label="TODAY" size="small" sx={{ 
                      height: 16, 
                      fontSize: '0.6rem', 
                      background: '#00d4ff', 
                      color: '#000',
                      fontWeight: 800
                    }} />
                  )}
                </Box>
                <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100% - 40px)', px: 0.5 }}>
                  {dayTasks.map(task => (
                    <Tooltip 
                      key={task.id} 
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{task.name}</Typography>
                          <Typography variant="caption" display="block">{task.description}</Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: '#00d4ff' }}>
                            Time: {formatSafeDate(task.next_run, 'p')}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        mb: 1, 
                        p: 1, 
                        borderRadius: '8px',
                        background: alpha(getStatusColor(task.status), 0.1),
                        borderLeft: `3px solid ${getStatusColor(task.status)}`,
                        cursor: 'pointer',
                        '&:hover': {
                          background: alpha(getStatusColor(task.status), 0.2),
                        }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <img src={getServiceIcon(task.service)} alt="" style={{ width: 14, height: 14, marginRight: 6 }} />
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.7rem', 
                            color: '#00d4ff',
                            fontWeight: 700,
                            mr: 1
                          }}>
                            {task.next_run ? formatSafeDate(task.next_run, 'HH:mm') : ''}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            fontSize: '0.7rem', 
                            color: '#fff',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1
                          }}>
                            {task.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ 
                          fontSize: '0.65rem', 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontWeight: 500
                        }}>
                          {task.schedule || 'Scheduled'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                  {dayTasks.length === 0 && (
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 4, color: 'rgba(255, 255, 255, 0.15)', fontStyle: 'italic' }}>
                      No tasks
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ 
            color: '#fff', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box
              component="img"
              src="/task_scheduler.png"
              alt=""
              sx={{ width: 45, height: 45, filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.8))' }}
            />
            Task Scheduler
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
            Unified overview of all home lab scheduled maintenance and backups
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Paper sx={{ 
            display: 'flex', 
            background: 'rgba(255, 255, 255, 0.05)', 
            p: 0.5, 
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <IconButton 
              onClick={() => setView('list')}
              sx={{ 
                borderRadius: '8px',
                color: view === 'list' ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
                background: view === 'list' ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                '&:hover': { background: 'rgba(0, 212, 255, 0.2)' }
              }}
            >
              <ViewList />
            </IconButton>
            <IconButton 
              onClick={() => setView('calendar')}
              sx={{ 
                borderRadius: '8px',
                color: view === 'calendar' ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
                background: view === 'calendar' ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                '&:hover': { background: 'rgba(0, 212, 255, 0.2)' }
              }}
            >
              <EventNote />
            </IconButton>
          </Paper>
          <IconButton 
            onClick={fetchTasks} 
            disabled={loading}
            sx={{ 
              background: 'rgba(255, 255, 255, 0.05)', 
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <Refresh className={loading ? 'spin' : ''} />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {renderFilters()}

      {loading && tasks.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#00d4ff' }} />
        </Box>
      ) : (
        <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
          {view === 'list' ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <AccessTime sx={{ mr: 1, fontSize: 20 }} />
                  Upcoming Jobs
                </Typography>
              </Grid>
              {sortedTasks.map((task, index) => (
                <Grid item xs={12} md={6} lg={4} key={task.id}>
                  {renderTaskCard(task, index)}
                </Grid>
              ))}
              {filteredTasks.length === 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: 'center', background: 'rgba(255, 255, 255, 0.02)', border: '1px dashed rgba(255, 255, 255, 0.1)' }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                      {tasks.length === 0 ? 'No scheduled tasks found.' : 'No tasks match current filters.'}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : (
            renderCalendar()
          )}
        </Box>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </Box>
  );
};

export default TaskScheduler;
